// CampaignImpact.jsx — Cross-campaign impact dashboard.
// Shows return rates (7d/30d), spend uplift, and ROI per campaign so admin/marketing
// can see which campaigns actually brought customers back.
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { PhoneListUpload } from "./components/PhoneListUpload";
import CampaignProMetrics from "./CampaignProMetrics";
import { zVar } from "./lib/zIndex";

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }
function fmF(n, d = 1) { return (Number(n) || 0).toFixed(d); }

const SORT_OPTIONS = [
  { key: "efficiency_score",  label: "⭐ نقاط الكفاءة" },
  { key: "return_rate_30d",   label: "📈 معدل العودة 30 يوم" },
  { key: "spend_30d",         label: "💰 الإنفاق بعد الحملة" },
  { key: "incremental_revenue", label: "📊 الإيراد الإضافي" },
  { key: "roi_pct",           label: "⚡ ROI" },
  { key: "new_customers",     label: "🆕 العملاء الجدد" },
  { key: "start_date",        label: "🗓 الأحدث" },
];

export default function CampaignImpact() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState("efficiency_score");
  const [filterBlast, setFilterBlast] = useState("all"); // all | blast | regular | ambassador
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data: d } = await api.get("/campaigns/impact-overview");
      setData(d);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // (د) رسالة سبب عربية محددة لقياس الأثر بدل «حدث خطأ ما» العام
      setErr(detail
        ? formatErr(detail)
        : "تعذّر حساب قياس الأثر — تأكّد من تحميل بيانات الفواتير والحملات ثم اضغط تحديث.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    const arr = data?.items || [];
    let out = arr;
    if (filterBlast === "blast") out = arr.filter((r) => r.is_blast);
    else if (filterBlast === "regular") out = arr.filter((r) => !r.is_blast && !r.is_ambassador);
    else if (filterBlast === "ambassador") out = arr.filter((r) => r.is_ambassador);
    return [...out].sort((a, b) => {
      const av = a[sortBy] ?? -Infinity;
      const bv = b[sortBy] ?? -Infinity;
      if (sortBy === "start_date") return String(bv).localeCompare(String(av));
      return (Number(bv) || 0) - (Number(av) || 0);
    });
  }, [data, sortBy, filterBlast]);

  const totals = data?.totals;

  if (loading) return <div className="card" data-testid="impact-loading">⏳ جارٍ التحميل...</div>;
  if (err) return <div className="al alr" data-testid="impact-error">{err}</div>;
  if (!data || (data.items || []).length === 0) {
    return (
      <div className="card" data-testid="impact-empty" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 36 }}>📊</div>
        <h3 style={{ color: "var(--brown-deep)" }}>لا توجد حملات بعد</h3>
        <p style={{ fontSize: 12.5, color: "var(--muted)" }}>
          أنشئ حملة (سواء عادية أو حملة استرداد من صفحة العملاء) — وستظهر نتائجها هنا فور توفّر بيانات الفواتير.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="campaign-impact-page">
      {/* Hero KPIs */}
      <div style={{
        marginBottom: 14,
        padding: "18px 22px",
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 100%)",
        color: "#fff", borderRadius: 14,
      }} data-testid="impact-hero">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 1 }}>أثر الحملات</div>
            <h2 style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 800 }}>
              📊 محرك CRM — ما الذي يشتغل فعلاً؟
            </h2>
          </div>
          <button onClick={load} className="btn btn-soft" data-testid="impact-refresh"
                  style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}>
            🔄 تحديث
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginTop: 16 }}>
          <HeroTile label="حملات" value={totals?.campaigns ?? 0} sub={totals?.ambassadors ? `+ ${fm(totals.ambassadors)} شريك` : "—"} />
          <HeroTile label="🏆 ناجحة" value={totals?.winners ?? 0} sub={`${totals?.losers ?? 0} ضعيفة/خاسرة`} highlight />
          <HeroTile label="رسائل/استخدامات" value={fm(totals?.total_sent ?? 0)} />
          <HeroTile label="عادوا 30 يوم" value={fm(totals?.total_returned_30d ?? 0)} sub={totals?.total_sent ? `${fmF(totals.total_returned_30d / totals.total_sent * 100)}%` : "—"} />
          <HeroTile label="إيراد إضافي" value={`${fm(totals?.total_incremental ?? 0)} ر`} sub="مقابل ما كانوا ينفقونه" />
          <HeroTile label="عملاء جدد" value={fm(totals?.total_new_customers ?? 0)} />
          <HeroTile label="تكلفة" value={`${fm(totals?.total_cost ?? 0)} ر`} />
        </div>
      </div>

      {/* Marketing-consultant-grade pro metrics (statistical significance,
          CIC, RLR, cannibalization, etc.) — built client-side from items[] */}
      <CampaignProMetrics items={data?.items || []} />

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4, background: "var(--beige)", padding: 4, borderRadius: 10 }} data-testid="impact-filter">
          {[
            { k: "all", l: "الكل" },
            { k: "blast", l: "🚀 استرداد" },
            { k: "regular", l: "📅 منتظمة" },
            { k: "ambassador", l: "🌟 شركاء" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilterBlast(f.k)} data-testid={`impact-filter-${f.k}`}
                    style={{
                      padding: "6px 14px", fontSize: 12, fontWeight: 600, border: "none",
                      background: filterBlast === f.k ? "var(--brown)" : "transparent",
                      color: filterBlast === f.k ? "#fff" : "var(--brown-deep)",
                      borderRadius: 6, cursor: "pointer", transition: "all 160ms ease",
                    }}>
              {f.l}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="impact-sort"
                style={{ padding: "7px 12px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}>
          {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>ترتيب حسب: {o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="impact-table-wrap">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "var(--beige)" }}>
              <tr>
                <th style={th}>الحملة / الشريك</th>
                <th style={thC}>النوع</th>
                <th style={thC}>الحكم</th>
                <th style={thC}>الكفاءة</th>
                <th style={thC} data-testid="impact-th-audience">المستهدفون / المُرسل</th>
                <th style={thC}>عاد 30 يوم</th>
                <th style={thC}>إنفاق 30 يوم</th>
                <th style={thC}>إيراد إضافي</th>
                <th style={thC}>عملاء جدد</th>
                <th style={thC}>تكرار</th>
                <th style={thC}>ROI</th>
                <th style={thC}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const r30 = c.return_rate_30d;
                const isBlast = c.is_blast;
                const isAmb = c.is_ambassador;
                const isWinner = (c.efficiency_score || 0) >= 75;
                return (
                  <React.Fragment key={c.id}>
                  <tr data-testid={`impact-row-${c.id}`} style={{
                    borderTop: "1px solid var(--border)",
                    background: isWinner ? "rgba(16,185,129,0.04)" : (c.efficiency_score < 35 && c.sent_count > 0 ? "rgba(220,38,38,0.04)" : "transparent"),
                    cursor: "pointer",
                  }} onClick={() => setSelected(c)}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: "var(--brown-deep)" }}>
                        {isWinner && "🏆 "}{c.name}
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>
                        {c.segment_label}{!isAmb && c.start_date && ` · ${c.start_date} → ${c.end_date || "—"}`}
                      </div>
                    </td>
                    <td style={tdC}>
                      <span style={{
                        background: isAmb ? "rgba(124,58,237,0.12)" : (isBlast ? "rgba(37,211,102,0.12)" : "rgba(107,78,61,0.10)"),
                        color: isAmb ? "#7C3AED" : (isBlast ? "#128C7E" : "var(--brown)"),
                        padding: "2px 8px", borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                      }}>
                        {isAmb ? "🌟 شريك" : (isBlast ? "🚀 استرداد" : "📅 منتظمة")}
                      </span>
                    </td>
                    <td style={tdC}>
                      <span style={{
                        background: `${c.verdict_color}20`,
                        color: c.verdict_color,
                        padding: "3px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                        whiteSpace: "nowrap",
                      }} data-testid={`impact-verdict-${c.id}`}>
                        {c.verdict_label || "—"}
                      </span>
                    </td>
                    <td style={tdC}>
                      <ScoreBar score={c.efficiency_score || 0} />
                    </td>
                    <td style={tdC} data-testid={`impact-audience-${c.id}`}>
                      {fm(isBlast || isAmb ? c.sent_count : (c.targeted_count ?? c.sent_count))}
                      <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 2 }}>
                        {isAmb ? "استخدامات" : (isBlast ? "المُرسل" : "المستهدفون")}
                      </div>
                    </td>
                    <td style={tdC}>
                      <ReturnCell count={c.returned_30d} rate={r30} primary />
                    </td>
                    <td style={tdC}>
                      <b style={{ color: c.spend_30d > 0 ? "var(--success)" : "var(--muted)" }}>
                        {fm(c.spend_30d)} ر
                      </b>
                    </td>
                    <td style={tdC}>
                      <b style={{ color: (c.incremental_revenue || 0) > 0 ? "var(--success)" : (c.incremental_revenue || 0) < 0 ? "var(--danger)" : "var(--muted)" }}>
                        {(c.incremental_revenue || 0) > 0 ? "+" : ""}{fm(c.incremental_revenue || 0)} ر
                      </b>
                    </td>
                    <td style={tdC}>
                      {c.new_customers > 0 ? (
                        <span style={{ color: "var(--success)", fontWeight: 700 }}>+{fm(c.new_customers)}</span>
                      ) : <span style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                    <td style={tdC}>
                      {c.repeat_rate_pct > 0 ? `${fmF(c.repeat_rate_pct, 0)}%` : <span style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                    <td style={tdC}>
                      {c.roi_pct === null || c.roi_pct === undefined ? (
                        <span style={{ color: "var(--muted)" }}>—</span>
                      ) : (
                        <span style={{ color: c.roi_pct > 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
                          {c.roi_pct > 0 ? "+" : ""}{fmF(c.roi_pct, 0)}%
                        </span>
                      )}
                    </td>
                    <td style={tdC}>
                      <button onClick={(e) => { e.stopPropagation(); setSelected(c); }}
                              data-testid={`impact-details-btn-${c.id}`}
                              style={{
                                padding: "4px 10px", fontSize: 11, background: "var(--beige)",
                                color: "var(--brown-deep)", border: "none", borderRadius: 4,
                                cursor: "pointer", fontWeight: 600,
                              }}>
                        تفاصيل
                      </button>
                    </td>
                  </tr>
                  {c.insight && (
                    <tr style={{ background: "rgba(0,0,0,0.015)" }}>
                      <td colSpan={12} style={{ padding: "6px 14px", fontSize: 11.5, color: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
                        <span style={{ color: c.verdict_color, fontWeight: 700 }}>💡 رؤية:</span> {c.insight}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insight footer */}
      <div style={{
        marginTop: 14, padding: "14px 18px",
        background: "linear-gradient(135deg, rgba(194,169,127,0.08) 0%, transparent 100%)",
        borderInlineStart: "3px solid var(--gold)", borderRadius: 6,
        fontSize: 12, color: "var(--brown-deep)", lineHeight: 1.9,
      }} data-testid="impact-insight-tip">
        💡 <b>كيف نقرأ هذه الأرقام؟</b><br />
        <b>نقاط الكفاءة (0-100)</b> هي المقياس الذهبي الشامل — تجمع معدل العودة + الإيراد لكل رسالة + ROI.
        <b>الإيراد الإضافي</b> = ما أنفقه العملاء بعد الحملة − ما كانوا ينفقونه قبلها (الزيادة الحقيقية).
        <b>العملاء الجدد</b> = من زاروا لأول مرة بسبب الحملة. <b>الشركاء 🌟</b> يُحسبون كحملات مستمرة (كل استخدام كود = «إرسال» + «عودة» تلقائياً).
        كل صف بحكم تلقائي 🏆/✅/⚠️/❌ ورؤية مختصرة.
      </div>

      {/* Drawer with full details (uses existing /impact endpoint) */}
      {selected && (selected.is_ambassador
        ? <AmbassadorImpactDrawer row={selected} onClose={() => setSelected(null)} />
        : <ImpactDrawer campaign={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

// ─── Ambassador inline drawer ─────────────────────────────────────────────
function AmbassadorImpactDrawer({ row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!row.ambassador_id) { setLoading(false); return; }
    let alive = true;
    api.get(`/ambassadors/${row.ambassador_id}`)
      .then(({ data }) => { if (alive) setDetail(data); })
      .catch((e) => { if (alive) setErr(formatErr(e.response?.data?.detail) || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [row.ambassador_id]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", justifyContent: "flex-start", alignItems: "stretch", zIndex: zVar('extra_1000'),
    }} data-testid="amb-impact-drawer-backdrop">
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(620px, 100%)", background: "#fff", overflow: "auto",
        padding: 22, boxShadow: "0 0 40px rgba(0,0,0,0.3)",
      }} data-testid="amb-impact-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>{row.name}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--muted)" }}>×</button>
        </div>

        <div style={{
          padding: "12px 14px", background: `${row.verdict_color}10`,
          borderInlineStart: `3px solid ${row.verdict_color}`, borderRadius: 8, marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: row.verdict_color }}>
            {row.verdict_label} · {row.efficiency_score} / 100
          </div>
          <div style={{ fontSize: 12, color: "var(--brown-deep)", marginTop: 4 }}>💡 {row.insight}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
          <DKpi label="استخدامات" value={fm(row.sent_count)} />
          <DKpi label="الإيراد الإجمالي" value={`${fm(row.spend_30d)} ر`} color="var(--success)" />
          <DKpi label="الإيراد الإضافي" value={`${fm(row.incremental_revenue || 0)} ر`} color="#7C3AED" />
          <DKpi label="متوسط الفاتورة" value={`${fm(row.avg_invoice_during)} ر`} />
          <DKpi label="عمولة" value={`${fm(row.cost_sar)} ر`} color="#D97706" />
          <DKpi label="ROI" value={row.roi_pct === null ? "—" : `${row.roi_pct > 0 ? "+" : ""}${row.roi_pct}%`} color={row.roi_pct > 0 ? "var(--success)" : "var(--danger)"} />
        </div>

        {loading && <div style={{ color: "var(--muted)" }}>جارٍ التحميل…</div>}
        {err && <div className="al alr">{err}</div>}
        {detail?.redemptions && detail.redemptions.length > 0 && (
          <div>
            <h4 style={{ marginTop: 14 }}>📜 آخر الاستخدامات</h4>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "var(--beige)" }}>
                <th style={th}>التاريخ</th><th style={th}>المبلغ</th><th style={th}>العمولة</th><th style={th}>الفرع</th>
              </tr></thead>
              <tbody>
                {detail.redemptions.slice(0, 30).map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}>{(r.redeemed_at || "").slice(0, 10)}</td>
                    <td style={td}>{fm(r.amount)}</td>
                    <td style={{ ...td, color: "#D97706" }}>{fm(r.commission_earned)}</td>
                    <td style={td}>{r.branch || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 11, color: "var(--muted)" }}>
          🔒 الشركاء يُحسبون كحملات مستمرة. لإدارة الشريك كاملاً، اذهب إلى تبويب 🌟 شركاء ثواني.
        </div>
      </div>
    </div>
  );
}

function DKpi({ label, value, color }) {
  return (
    <div style={{ padding: 10, background: "var(--beige)", borderRadius: 8 }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: color || "var(--brown-deep)", marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────
const th = { padding: "10px 14px", textAlign: "start", fontSize: 11.5, color: "var(--muted)", fontWeight: 700 };
const thC = { padding: "10px 8px", textAlign: "center", fontSize: 11.5, color: "var(--muted)", fontWeight: 700 };
const td = { padding: "12px 14px" };
const tdC = { padding: "12px 8px", textAlign: "center" };

function HeroTile({ label, value, sub, highlight }) {
  return (
    <div style={{
      padding: "10px 12px", borderRadius: 8,
      background: highlight ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
      border: highlight ? "1.5px solid rgba(255,255,255,0.35)" : "none",
    }}>
      <div style={{ fontSize: 10.5, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 75 ? "#059669" : score >= 55 ? "#0284C7" : score >= 35 ? "#D97706" : "#B91C1C";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 60, height: 6, background: "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.max(2, Math.min(100, score))}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ color, fontWeight: 800, fontSize: 12, minWidth: 24, textAlign: "center" }}>{score}</span>
    </div>
  );
}

function ReturnCell({ count, rate, primary }) {
  const color = rate >= 30 ? "var(--success)" : rate >= 15 ? "var(--warn)" : rate > 0 ? "var(--brown)" : "var(--muted)";
  return (
    <div>
      <div style={{ fontWeight: 700, color, fontSize: primary ? 14 : 13 }}>
        {fm(count)} <span style={{ fontSize: 10, opacity: 0.8 }}>({fmF(rate)}%)</span>
      </div>
      {primary && (
        <div style={{ width: 60, height: 4, background: "var(--border)", borderRadius: 2, margin: "4px auto 0", overflow: "hidden" }}>
          <div style={{ width: Math.min(100, rate) + "%", height: "100%", background: color, transition: "width 400ms ease" }} />
        </div>
      )}
    </div>
  );
}

// ─── Drawer: detailed per-recipient list ──────────────────────────────────
function ImpactDrawer({ campaign, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [breakdownTab, setBreakdownTab] = useState("paying");
  const [showRebuild, setShowRebuild] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/campaigns/${campaign.id}/impact`)
      .then(({ data }) => { if (alive) setDetail(data); })
      .catch((e) => { if (alive) setErr(formatErr(e.response?.data?.detail) || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [campaign.id, refreshKey]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", justifyContent: "flex-start", alignItems: "stretch",
      zIndex: zVar('extra_1000'),
    }} data-testid="impact-drawer-backdrop">
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(720px, 100%)", background: "#fff",
        overflow: "auto", padding: 22, boxShadow: "0 0 40px rgba(0,0,0,0.3)",
      }} data-testid="impact-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>{campaign.name}</h3>
          <button onClick={onClose} data-testid="impact-drawer-close" style={{
            background: "var(--beige)", border: "none", width: 30, height: 30,
            borderRadius: "50%", cursor: "pointer", fontSize: 14,
          }}>✕</button>
        </div>

        {loading && <div>⏳ جارٍ التحميل...</div>}
        {err && <div className="al alr">{err}</div>}

        {detail && (
          <>
            <div className="g3" style={{ gap: 8, marginBottom: 10 }}>
              <MiniTile label="مستهدفين" value={fm(detail.totals.targeted)} />
              <MiniTile label="عادوا" value={fm(detail.totals.continued)} color="var(--success)" />
              <MiniTile label="معدل البقاء" value={`${fmF(detail.totals.retention_pct)}%`} color="var(--brown-deep)" />
            </div>

            {/* New breakdown — 5 categories the user explicitly asked for */}
            <div className="ci-breakdown-grid" data-testid="impact-breakdown-grid">
              <BreakdownTile l="💳 دافعون" v={detail.totals.paying} pct={detail.totals.paying_pct} testid="bk-paying" tone="success" sub={`+${fm(detail.totals.total_paid_spend_after_start)} ر`} />
              <BreakdownTile l="☕ خلّصوا أكوابهم" v={detail.totals.finished_cups} testid="bk-finished" tone="gold" sub="59x" />
              <BreakdownTile l="🔥 مستمرون" v={detail.totals.continuing} pct={detail.totals.continuing_pct} testid="bk-continuing" tone="brown" sub="عادوا بعد انتهاء الحملة" />
              <BreakdownTile l="😴 خاملون" v={detail.totals.dormant} testid="bk-dormant" tone="muted" sub="60+ يوم" />
            </div>

            <div style={{ marginBottom: 14, padding: 10, background: "var(--beige)", borderRadius: 8, fontSize: 12.5 }}>
              💰 إنفاق بعد الحملة: <b style={{ color: "var(--success)" }}>{fm(detail.totals.total_post_spend)} ر</b>
              {detail.resolved_from_segment && (
                <span style={{ marginInlineStart: 12, fontSize: 11, color: "var(--muted)" }} data-testid="impact-resolved-badge">
                  🎯 المستهدفون استُخرجوا تلقائياً من شريحة «{detail.campaign?.target_segment}»
                </span>
              )}
              {detail.campaign?.targets_last_rebuild && (
                <span style={{ marginInlineStart: 12, fontSize: 11, color: "var(--muted)" }}>
                  🔧 آخر إعادة بناء: {detail.campaign.targets_last_rebuild.source} (+{detail.campaign.targets_last_rebuild.added})
                </span>
              )}
            </div>

            {/* Rebuild button — extra prominent when targeted == 0 */}
            {detail.totals.targeted === 0 && (
              <div className="impact-rebuild-prompt" data-testid="impact-rebuild-prompt">
                <div>
                  ⚠ هذه الحملة بدون قائمة مستهدفين — لو الحملة تاريخية، تقدر تعيد بناء القائمة الآن.
                </div>
                <button type="button" className="btn btn-primary" onClick={() => setShowRebuild(true)} data-testid="impact-rebuild-btn-empty" style={{ fontSize: 12 }}>🔧 إعادة بناء قائمة المستهدفين</button>
              </div>
            )}

            {/* Toolbar: export + rebuild + selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaign.id}/impact/export.csv`;
                  const token = localStorage.getItem("token");
                  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                    .then((r) => r.blob())
                    .then((blob) => {
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `impact_${campaign.id.slice(0, 8)}.csv`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    });
                }}
                className="btn btn-outline"
                style={{ fontSize: 12 }}
                data-testid="impact-export-csv"
              >📥 تصدير CSV مع التأثير</a>
              <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setShowRebuild(true)} data-testid="impact-rebuild-btn">🔧 إعادة بناء القائمة</button>
              <select
                value={breakdownTab}
                onChange={(e) => setBreakdownTab(e.target.value)}
                className="sim-input"
                style={{ fontSize: 12, padding: "4px 8px", maxWidth: 240, marginInlineStart: "auto" }}
                data-testid="impact-breakdown-tab"
              >
                <option value="paying">💳 دافعون ({detail.breakdown?.paying?.length || 0})</option>
                <option value="finished_cups">☕ خلّصوا أكوابهم ({detail.breakdown?.finished_cups?.length || 0})</option>
                <option value="continuing">🔥 مستمرون ({detail.breakdown?.continuing?.length || 0})</option>
                <option value="dormant">😴 خاملون ({detail.breakdown?.dormant?.length || 0})</option>
                <option value="continued">✅ عادوا ({detail.continued.length})</option>
                <option value="churned">💔 لم يعودوا ({detail.churned.length})</option>
              </select>
            </div>

            {/* Active breakdown list */}
            <BreakdownList list={detail.breakdown?.[breakdownTab] ?? detail[breakdownTab] ?? []} kind={breakdownTab} />

            {showRebuild && (
              <RebuildTargetsModal
                campaignId={campaign.id}
                onClose={() => setShowRebuild(false)}
                onDone={() => { setShowRebuild(false); setRefreshKey((k) => k + 1); }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MiniTile({ label, value, color }) {
  return (
    <div style={{ padding: "10px 12px", background: "var(--beige)", borderRadius: 8, textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: color || "var(--brown-deep)" }}>{value}</div>
    </div>
  );
}

function RecipientRow({ r, positive }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 12px", marginBottom: 4, borderRadius: 6,
      background: positive ? "rgba(16,185,129,0.05)" : "rgba(217,119,6,0.04)",
      borderInlineStart: `3px solid ${positive ? "var(--success)" : "var(--warn)"}`,
      fontSize: 12,
    }}>
      <div>
        <b style={{ color: "var(--brown-deep)" }}>{r.name || "(بدون اسم)"}</b>
        <span style={{ fontSize: 10, color: "var(--muted)", marginInlineStart: 8 }} dir="ltr">{r.phone}</span>
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--brown)" }}>
        <span>زيارات: <b>{fm(r.after_visits)}</b></span>
        <span>أنفق: <b style={{ color: positive ? "var(--success)" : "var(--muted)" }}>{fm(r.after_spend)} ر</b></span>
      </div>
    </div>
  );
}

function BreakdownTile({ l, v, pct, sub, testid, tone = "brown" }) {
  const toneMap = {
    success: { bg: "#e8f5e9", border: "#a5d6a7", color: "#1b5e20" },
    gold: { bg: "#fff8e1", border: "#ffe082", color: "#8b6f47" },
    brown: { bg: "#faf6ee", border: "#d9c8a8", color: "#6d4c41" },
    muted: { bg: "#f5f5f5", border: "#e0e0e0", color: "#757575" },
  };
  const t = toneMap[tone] || toneMap.brown;
  return (
    <div data-testid={testid} style={{
      padding: "10px 12px", background: t.bg, border: `1px solid ${t.border}`,
      borderRadius: 8, minHeight: 80, display: "flex", flexDirection: "column", gap: 2,
    }}>
      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{l}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: t.color, fontFamily: "var(--font-num, Outfit, system-ui)" }}>{fm(v || 0)}</div>
        {pct !== undefined && pct !== null && (
          <div style={{ fontSize: 11.5, color: t.color, fontWeight: 600 }}>({fmF(pct)}%)</div>
        )}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{sub}</div>}
    </div>
  );
}

function BreakdownList({ list, kind }) {
  if (!list || list.length === 0) {
    return (
      <div style={{ color: "var(--muted)", fontSize: 12, textAlign: "center", padding: 18 }} data-testid={`impact-list-${kind}-empty`}>
        لا توجد بيانات في هذه الفئة لهذه الحملة.
      </div>
    );
  }
  return (
    <div data-testid={`impact-list-${kind}`}>
      {list.slice(0, 100).map((r, i) => (
        <BreakdownRow key={i} r={r} kind={kind} />
      ))}
      {list.length > 100 && (
        <div style={{ color: "var(--muted)", fontSize: 11, textAlign: "center", padding: 6 }}>
          + {list.length - 100} عميل آخر — استخدم تصدير CSV للقائمة الكاملة
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ r, kind }) {
  // Color theme by category
  const themes = {
    paying:        { bg: "rgba(16,185,129,0.06)", border: "var(--success)" },
    finished_cups: { bg: "rgba(201,164,74,0.08)", border: "#c9a44a" },
    continuing:    { bg: "rgba(109,76,65,0.06)",  border: "#8b6f47" },
    dormant:       { bg: "rgba(120,120,120,0.04)", border: "#9e9e9e" },
    continued:     { bg: "rgba(16,185,129,0.05)", border: "var(--success)" },
    churned:       { bg: "rgba(217,119,6,0.04)",  border: "var(--warn)" },
  };
  const t = themes[kind] || themes.continued;
  return (
    <div style={{
      padding: "8px 12px", marginBottom: 4, borderRadius: 6,
      background: t.bg, borderInlineStart: `3px solid ${t.border}`,
      fontSize: 12, display: "flex", flexDirection: "column", gap: 3,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <b style={{ color: "var(--brown-deep)" }}>{r.name || "(بدون اسم)"}</b>
          <span style={{ fontSize: 10, color: "var(--muted)", marginInlineStart: 8 }} dir="ltr">{r.phone}</span>
          {r.is59 && <span style={{ fontSize: 9.5, color: "#8b6f47", marginInlineStart: 6, padding: "1px 6px", background: "#fff8e1", borderRadius: 8 }}>59</span>}
          {r.is59x && <span style={{ fontSize: 9.5, color: "#6d4c41", marginInlineStart: 4, padding: "1px 6px", background: "#fff", borderRadius: 8, border: "1px solid #8b6f47" }}>59x</span>}
        </div>
        <span style={{ fontSize: 10.5, color: "var(--muted)" }}>
          {r.days_since_last !== null && r.days_since_last !== undefined ? `${r.days_since_last} يوم منذ آخر زيارة` : "—"}
        </span>
      </div>
      <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "var(--brown)", flexWrap: "wrap" }}>
        <span>إجمالي زيارات: <b>{fm(r.total_visits)}</b></span>
        <span>إجمالي إنفاق: <b>{fm(r.total_spent)} ر</b></span>
        {r.paid_invoices_after_start > 0 && (
          <span style={{ color: "var(--success)" }}>دفعات بعد الحملة: <b>{fm(r.paid_invoices_after_start)}</b> = <b>{fm(r.paid_spend_after_start)} ر</b></span>
        )}
        {r.after_visits > 0 && (
          <span>زيارات بعد الحملة: <b>{fm(r.after_visits)}</b></span>
        )}
      </div>
    </div>
  );
}



function RebuildTargetsModal({ campaignId, onClose, onDone }) {
  const [source, setSource] = useState("is59_or_is59x");
  const [phonesList, setPhonesList] = useState([]);
  const [merge, setMerge] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const body = { source, merge_with_existing: merge };
      if (source === "manual") {
        const phones = phonesList;
        if (phones.length === 0) {
          setError("ارفع ملف أرقام (Excel أو CSV) أولاً"); setLoading(false); return;
        }
        body.phones = phones;
      }
      if (source === "segment") body.segment = "is59";
      const { data } = await api.post(`/campaigns/${campaignId}/rebuild-targets`, body);
      setResult(data);
      // Auto-close + refresh after a short delay so user sees confirmation
      setTimeout(onDone, 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "فشلت العملية");
    }
    setLoading(false);
  };

  return (
    <div className="fin-modal" onClick={onClose} data-testid="rebuild-modal">
      <div className="fin-modal-body" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="fin-modal-h">
          <h3 style={{ margin: 0 }}>🔧 إعادة بناء قائمة المستهدفين</h3>
          <button type="button" className="btn btn-outline" onClick={onClose} data-testid="rebuild-close">✖</button>
        </div>
        <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7, marginBottom: 12 }}>
          استخدم هذه الأداة للحملات التاريخية التي لم تُسجَّل قائمة مستهدفينها أصلاً، أو لإضافة عملاء جدد لحملة موجودة.
        </p>

        <div className="rebuild-options">
          <Opt v="is59_or_is59x" cur={source} set={setSource} testid="src-59-or-59x" t="🎁 كل المسجلين في برنامج 59 كوب"
               sub="عملاء عندهم flag 59 + عملاء خلّصوا أكوابهم (59x). الأنسب للحملة التاريخية." />
          <Opt v="is59x_from_period" cur={source} set={setSource} testid="src-59x-period" t="☕ خلّصوا 59 كوب خلال فترة الحملة"
               sub="عملاء وصلوا is59x وآخر زيارة لهم بين تاريخ بداية ونهاية الحملة." />
          <Opt v="invoices_during_period" cur={source} set={setSource} testid="src-invoices" t="📊 كل العملاء النشطين خلال فترة الحملة"
               sub="أي عميل عنده فاتورة بين تاريخ بداية الحملة ونهايتها." />
          <Opt v="segment" cur={source} set={setSource} testid="src-segment" t="🎯 من شريحة is59 (نشطون فقط)"
               sub="فقط الذين لم يخلّصوا أكوابهم بعد." />
          <Opt v="manual" cur={source} set={setSource} testid="src-manual" t="📤 رفع ملف أرقام"
               sub="ارفع ملف Excel/CSV — تُطبَّع الأرقام تلقائياً." />
          <Opt v="all_customers" cur={source} set={setSource} testid="src-all" t="👥 كل عملاء قاعدة البيانات"
               sub="استخدم هذا الخيار بحذر — يضم الجميع." />
        </div>

        {source === "manual" && (
          <div style={{ marginTop: 10 }}>
            <PhoneListUpload
              testid="rebuild-phones-upload"
              onChange={(phones) => setPhonesList(phones)}
            />
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12, color: "var(--brown-deep)" }}>
          <input type="checkbox" checked={merge} onChange={(e) => setMerge(e.target.checked)} data-testid="rebuild-merge" />
          دمج مع القائمة الموجودة (بدلاً من الاستبدال)
        </label>

        {error && <div className="fin-error" data-testid="rebuild-error">{error}</div>}
        {result && (
          <div className="fin-success" data-testid="rebuild-success">
            ✓ تم — أُضيف {result.added} عميل، وحُذف {result.removed}، الإجمالي الآن {result.total}.
          </div>
        )}

        <div className="fin-modal-foot">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>إلغاء</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={loading} data-testid="rebuild-submit">
            {loading ? "⏳ يجري التحديث…" : "🔧 إعادة البناء"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Opt({ v, cur, set, testid, t, sub }) {
  const active = v === cur;
  return (
    <label
      data-testid={testid}
      onClick={() => set(v)}
      style={{
        display: "block", padding: "10px 12px", marginBottom: 6, cursor: "pointer",
        background: active ? "rgba(201,164,74,0.12)" : "var(--card)",
        border: `1px solid ${active ? "var(--gold-deep, #c9a44a)" : "var(--border)"}`,
        borderRadius: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input type="radio" checked={active} onChange={() => set(v)} style={{ accentColor: "var(--gold-deep, #c9a44a)" }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)" }}>{t}</span>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginInlineStart: 24, marginTop: 4, lineHeight: 1.6 }}>{sub}</div>
    </label>
  );
}
